import { nanoid } from "@reduxjs/toolkit";
import moment from "moment";
import agirlikBirimOptions from "../../utils/yukOptions/agirlikBirimOptions";
import fiyatlandirmaTipOptions from "../../utils/yukOptions/fiyatlandirmaTipOptions";
import komisyonDurumuOptions from "../../utils/yukOptions/komisyonDurumuOptions";
import odemeSekliOptions from "../../utils/yukOptions/odemeSekliOptions";
import paketlemeItemDimensions from "../../utils/yukOptions/paketlemeItemDimension";
import sevkBicimleriOptions from "../../utils/yukOptions/sevkBicimleriOptions";
import yakitDurumuOptions from "../../utils/yukOptions/yakitDurumuOptions";
import yuklemeTuruOptions from "../../utils/yukOptions/yüklemeTuruOptions";
import { unwrapApiList } from "./registeredLoadPayload";

const formatDate = (value) => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : "";
};

const updateReplicator = (data = {}, aracTypes = []) => {
  const paymentDataResolver = {
    hedefNakliyefiyat: data.freight ?? 0,
    yukverenFiyat: data.loadOwnerFreight ?? 0,
    fiyatlandirmaType:
      fiyatlandirmaTipOptions.find((item) => item.value === data.paymentUnit) ??
      fiyatlandirmaTipOptions[0],
    fiyatYayinlansin: Boolean(data.freightVisibility),
    odemeSekli:
      odemeSekliOptions.find((item) => item.value === data.paymentTypeId) ??
      { value: "", text: "" },
    odemeSekliDigerAciklama: data.otherPaymentDescription ?? "",
    yakitDurumu:
      yakitDurumuOptions.find((item) => item.value === data.fuelPaymentId) ??
      { value: 0, text: "" },
    yakitOrani: data.fuelPayment ?? 0,
    komisyonDurumu:
      komisyonDurumuOptions.find(
        (item) => item.value === data.commissionPaymentId
      ) ?? { value: 0, text: "" },
    komisyonMiktari: data.commissionPayment ?? 0,
  };

  const selectedTruck = unwrapApiList(aracTypes).find(
    (item) => item?.id === data.truckTypeId
  );

  const aracDataResolver = {
    aracSayisi: data.truckQuantity ?? 1,
    aracTypeName: data.truckType ?? selectedTruck?.name ?? "",
    aracTypeId: data.truckTypeId ?? "",
    kasaTypeName: data.trailerType ?? "",
    kasaTypeId: data.trailerTypeId ?? "",
    kasaTypeOptions: unwrapApiList(selectedTruck?.trailerTypesModel),
    zeminType: {
      value: data.groundTypeId ?? 0,
      text: data.groundType ?? "Farketmez",
    },
    digerOzellikler: data.truckDescription ?? "",
  };

  const yukDataResolver = {
    yuklemeTuru:
      yuklemeTuruOptions.find((item) => item.value === data.isParsiyel) ??
      yuklemeTuruOptions[0],
    yukTuru: data.loadType ?? "",
    yukADR: Boolean(data.isDangerous),
    yukAdrDescription: data.dangerousDescription ?? "",
    agirlikBirim:
      agirlikBirimOptions.find((item) => item.value === data.loadUnitId) ??
      { value: "", text: "" },
    toplamAgirlik: data.loadQuantity ?? "",
    toplamHacim: data.loadBulk ?? 0,
    yukDesc: data.loadDescription ?? "",
    yukunDegeri: data.loadValue ?? 0,
    ustUsteYuklenmesin: Boolean(data.isStacked),
    companyLoadNumber: data.companyLoadNumber ?? "",
    customsPlace: data.customsPlace ?? "",
    vesselDate: data.vesselDate ? moment(data.vesselDate).format("YYYY-MM-DD") : "",
    isFreeZone: Boolean(data.isFreeZone),
  };

  const routeResolver = unwrapApiList(data.routePoints).map((item) => {
    const loadMethods = unwrapApiList(item?.loadMethods);

    return {
      id: item?.id ?? nanoid(),
      locationData: {
        il: item?.city ?? "",
        ilce: item?.town ?? "",
        lat: item?.lat ?? "",
        lng: item?.lng ?? "",
        location: item?.location ?? "",
        googleLat: item?.googleLat ?? item?.lat ?? "",
        googleLng: item?.googleLng ?? item?.lng ?? "",
        googleLocation: item?.googleAddress ?? "",
        googleSearchText: item?.googleAddress ?? "",
        isGoogleAddressSelected: Boolean(item?.isGoogleAddressSelected),
        isfirstChanged: false,
        bolge: "",
        regionId: item?.regionId ?? 0,
        ilId: "",
        ilceId: "",
      },
      companyName: item?.companyName ?? "",
      mailAddress: item?.mailAddress ?? "",
      routePointPersonName: item?.routePointPersonName ?? "",
      routePointPersonPhoneNumber: item?.routePointPersonPhoneNumber ?? "",
      smsVerification: Boolean(item?.smsVerification),
      smsVerificationPhoneNumber: item?.smsVerificationPhoneNumber ?? "",
      adres: item?.address ?? "",
      adresTarif: item?.forDirections ?? "",
      adresKaydedilsinMi: Boolean(item?.isAddressSaved),
      adresBaslik: item?.savedAddressName ?? "",
      date: formatDate(item?.loadDate),
      plainDate: item?.loadDate ?? "",
      dateEngec: formatDate(item?.lateLoadDate ?? item?.loadDate),
      plainDateEngec: item?.lateLoadDate ?? item?.loadDate ?? "",
      saatBaslangic: item?.loadTime ?? "00:00",
      saatBitis: item?.lateLoadTime ?? "00:00",
      order: item?.order ?? 0,
      saatAllTime:
        Boolean(item?.loadAllTime) ||
        (item?.loadTime === "00:00" && item?.lateLoadTime === "00:00"),
      locationType: item?.loadOrUnload === 0 ? "yukleme" : "bosaltma",
      sevkBicimleri: sevkBicimleriOptions.map((methodOption) => {
        const savedMethod = loadMethods.find(
          (method) => method?.methodId === methodOption.id
        );

        return {
          id: savedMethod?.id ?? methodOption.id,
          methodId: methodOption.id,
          name: methodOption.name,
          status: Boolean(savedMethod),
        };
      }),
    };
  });

  const paketlemeDataResolver = unwrapApiList(data.packingingTypes).map(
    (item) => {
      const packagingType = paketlemeItemDimensions.find(
        (option) => option.id === item?.packagingUnitId
      );

      return {
        id: item?.id ?? nanoid(),
        activePaketlemeTypeId: item?.packagingUnitId ?? 0,
        paketlemeType: packagingType?.name ?? "",
        list: unwrapApiList(item?.olculers),
      };
    }
  );

  return {
    paymentDataResolver,
    aracDataResolver,
    yukDataResolver,
    routeResolver,
    paketlemeDataResolver,
  };
};

export default updateReplicator;
